import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Link,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GpsFixedOutlined from '@mui/icons-material/GpsFixedOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import moment from 'moment';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import {
    manageAudition,
    publishAudition,
    sortApplication,
    unPublishAudition,
    updateAudition,
} from '../../redux/audition/actions';
import { managedAuditionWithSubmissionsSelector } from '../../selectors/selectors';
import { generateShareLink, pickAuditionCreatedTimestamp, useDispatchPromise } from '../../utils/AppUtils';
import { usePagination } from '../../hook/usePagination';
import { ManageAuditionContainer } from '../../components/ScreenContainer/ManageAuditionContainer';
import { ResourceTeamPanel } from '../../components/BrandTeam/ResourceTeamPanel';
import { IApplication } from '../../types/global/ListRender.types';
import { ICreateAuditionPayload } from '../../redux/audition/types';
import { ROUTES } from '../../types/global/routes.types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { CoverImageRenderer } from '../../components/AuditionComponents/CoverImageRenderer';
import { CustomChipGroup } from '../../components/Chip/CustomChipGroup';
import { DialogueSheet } from '../../components/DialogueSheet/DialogueSheet';
import { LinkBox } from '../../components/LinkBox/LinkBox';
import { RichTextViewer } from '../../components/Common/RichTextViewer';
import { SectionLabel, StatusChip } from './BrandCampaignDetailPage';
import { OverviewCard } from './campaignDetail/CampaignOverviewCards';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';

/** Matches `StatusChip` “published” pill: mint on tinted fill, fully rounded. */
const brandPublishedPillSx: SxProps<Theme> = {
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 11,
    height: 24,
    textTransform: 'capitalize',
    borderRadius: 999,
    bgcolor: `${BRAND_LUXE_SECONDARY}18`,
    color: BRAND_LUXE_SECONDARY,
    borderColor: 'transparent',
    '&:hover': {
        bgcolor: `${BRAND_LUXE_SECONDARY}28`,
    },
};

/** Fallback pill when a field has no value. */
const nonePillSx: SxProps<Theme> = {
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 11,
    height: 24,
    textTransform: 'capitalize',
    borderRadius: 999,
    bgcolor: '#0b0b0b',
    color: '#fff',
    borderColor: 'transparent',
    '&:hover': {
        bgcolor: '#121212',
    },
};

type UpdateField =
    | { key: keyof ICreateAuditionPayload; value: any }
    | { key: keyof ICreateAuditionPayload; value: any }[];

export default function BrandAuditionDetailPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const { auditionId } = useParams<{ auditionId: string }>();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [sortOption, setSortOption] = useState<string>('newest');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination(1, 6);
    const forceNoRefresh = useRef<boolean>(false);

    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);

    const {
        audition,
        applications,
        loading,
        error,
        searching,
        pagination: auditionPagination,
    } = useSelector(managedAuditionWithSubmissionsSelector);

    const openDetailsEditRef = useRef<(() => void) | null>(null);
    const registerOpenDetailsEdit = useCallback((open: () => void) => {
        openDetailsEditRef.current = open;
    }, []);

    const getData = useCallback(
        async (withClear?: boolean) => {
            if (!auditionId) return;
            const response: any = await dispatchPromise(
                manageAudition({
                    auditionId,
                    page: pagination.page,
                    limit: pagination.rows,
                    status: filterStatus !== 'all' ? filterStatus : undefined,
                    ...(searchTerm && { search: searchTerm }),
                    isSearch: forceNoRefresh.current,
                    sort: sortOption,
                    ...(roleFilter && roleFilter !== 'all' && { roleId: roleFilter }),
                }),
            );

            const { data } = response;
            if (data && data.pagination) {
                const {
                    pagination: { currentPage, pageSize, totalCount },
                } = data;
                setPagination({
                    page: currentPage,
                    rows: pageSize,
                    totalCount: totalCount,
                });
            }
            forceNoRefresh.current = false;
            if (withClear) {
                // pagination updated from response when applicable
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pagination.page, pagination.rows, sortOption, roleFilter, filterStatus, auditionId, searchTerm],
    );

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        if (applications?.length >= 0 && auditionPagination.total) {
            setPagination(prev => ({
                ...prev,
                totalCount: auditionPagination.total,
            }));
        }
    }, [applications, setPagination, auditionPagination.total]);

    const handleSearchChange = (searchText: string) => {
        forceNoRefresh.current = true;
        setSearchTerm(searchText);
    };

    const handleFilterStatusChange = (status: string) => {
        setFilterStatus(status);
    };

    const handleSortOptionChange = (option: string) => {
        setSortOption(option);
    };

    const handleRoleFilterChange = (roleId: string) => {
        setRoleFilter(roleId);
    };

    const handleAcceptApplication = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'yes',
            }),
        );
    };

    const handleRejectApplication = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'no',
            }),
        );
    };

    const handleMessageApplicant = (_application: IApplication) => {};

    const handleArchiveApplication = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'archived',
            }),
        );
    };

    const handleCastApplicant = async (application: IApplication) => {
        await dispatchPromise(
            sortApplication({
                id: application._id,
                sortType: 'cast',
            }),
        );
    };

    const handleUpdateField = useCallback(
        (input: UpdateField) => {
            const id = audition?._id || auditionId;
            if (!id) return;

            let body: Partial<ICreateAuditionPayload> = {};
            if (Array.isArray(input)) {
                input.forEach(({ key, value }) => {
                    body[key] = value;
                });
            } else {
                body[input.key] = input.value;
            }

            dispatch(
                updateAudition({
                    id,
                    body: body as ICreateAuditionPayload,
                    isImageUpload: false,
                }),
            );
        },
        [audition?._id, auditionId, dispatch],
    );

    const isPublished = audition?.status === 'published';
    const applicationCount = applications?.length ?? 0;

    const handlePublishSwitchChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (checked) {
            if (applicationCount > 0) {
                setPublishDialogOpen(true);
            } else {
                dispatch(publishAudition(audition?._id));
            }
        } else if (applicationCount > 0) {
            setUnpublishDialogOpen(true);
        } else {
            dispatch(unPublishAudition(audition?._id));
        }
    };

    const highlightButtonSx = {
        py: 1.25,
        borderRadius: 2,
        textTransform: 'none' as const,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: 13,
        bgcolor: t.primary_orange,
        border: `1px solid ${t.primary_orange}`,
        color: '#131517',
        '&:hover': { bgcolor: t.primary_orange },
        '& .MuiButton-startIcon': { color: '#131517' },
    };

    if (!auditionId) {
        return null;
    }

    if (!audition && loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    if (error && !audition) {
        return (
            <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)' }}>Could not load this audition.</Typography>
                <Button sx={{ mt: 2, color: BRAND_LUXE_PRIMARY }} onClick={() => navigate(ROUTES.BRAND_CAMPAIGNS)}>
                    Back to campaigns
                </Button>
            </Box>
        );
    }

    if (!audition) {
        return null;
    }

    const isCastingClosed = !!audition.casting_expiration && moment(audition.casting_expiration).isBefore(moment());
    const createdRaw = pickAuditionCreatedTimestamp(audition);

    const toTitleCase = (input: string) =>
        input
            .split(/[\s_-]+/)
            .filter(Boolean)
            .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' ');

    const unionLabel = audition.union_affiliation ? toTitleCase(String(audition.union_affiliation)) : '';
    const tagPills = (audition.tags || []).map(tag => toTitleCase(String(tag)));
    const hasTagValues = tagPills.length > 0;
    const gigTypePills = audition.gig_type ? [toTitleCase(String(audition.gig_type))] : [];

    const rawRequired = (audition.required_media || []).map(x => String(x ?? '').trim()).filter(Boolean);
    const requiredNonNone = rawRequired.filter(x => x.toLowerCase() !== 'none');
    const requiredMediaChipLabels = requiredNonNone.map(item => toTitleCase(item));
    const hasRequiredMediaValues = requiredMediaChipLabels.length > 0;

    const locationLabel = audition?.shoot_location?.address
        ? toTitleCase(String(audition.shoot_location.address))
        : 'Remote';

    return (
        <MetaTags title={`${audition.title} | Auditions`} description={audition.synopsis || audition.title}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}>
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        sx={{ alignSelf: 'flex-start', '& .MuiBreadcrumbs-separator': { color: t.form_label } }}>
                        <Link
                            component={RouterLink}
                            to={ROUTES.BRAND_CAMPAIGNS}
                            underline="hover"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: t.primary_white,
                                '&:hover': { color: BRAND_LUXE_PRIMARY },
                            }}>
                            Campaigns
                        </Link>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                            Auditions
                        </Typography>
                    </Breadcrumbs>
                    <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
                        <Tooltip title={isPublished ? 'Unpublish audition' : 'Publish audition'}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPublished}
                                        onChange={handlePublishSwitchChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_SECONDARY },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                bgcolor: `${BRAND_LUXE_SECONDARY}66`,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            color: isPublished ? BRAND_LUXE_SECONDARY : 'rgba(255,255,255,0.5)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}>
                                        {isPublished ? 'Published' : 'Draft'}
                                    </Typography>
                                }
                                labelPlacement="start"
                                sx={{ mr: 0, gap: 1 }}
                            />
                        </Tooltip>
                    </Box>
                </Box>

                {/* Header — aligned with BrandCampaignDetailPage */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'flex-start' },
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
                        <IconButton
                            onClick={() => navigate(ROUTES.BRAND_CAMPAIGNS)}
                            aria-label="Back to campaigns"
                            sx={{
                                color: BRAND_LUXE_PRIMARY,
                                bgcolor: 'rgba(255,201,101,0.08)',
                                '&:hover': { bgcolor: 'rgba(255,201,101,0.15)' },
                            }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: 22, md: 28 },
                                        color: '#fff',
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1.18,
                                    }}>
                                    {audition.title}
                                </Typography>
                                <StatusChip status={isPublished ? 'published' : 'draft'} />
                            </Box>
                            {createdRaw ? (
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 14,
                                        color: 'rgba(255,255,255,0.5)',
                                    }}>
                                    Created{' '}
                                    {new Date(createdRaw as string).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'center', md: 'center' },
                            justifyContent: { xs: 'space-between', md: 'flex-end' },
                            gap: 2,
                            flexShrink: 0,
                            width: { xs: '100%', md: 'auto' },
                            flexWrap: 'wrap',
                        }}>
                        <Tooltip title={isPublished ? 'Unpublish audition' : 'Publish audition'}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPublished}
                                        onChange={handlePublishSwitchChange}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_SECONDARY },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                bgcolor: `${BRAND_LUXE_SECONDARY}66`,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            color: isPublished ? BRAND_LUXE_SECONDARY : 'rgba(255,255,255,0.5)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}>
                                        {isPublished ? 'Published' : 'Draft'}
                                    </Typography>
                                }
                                labelPlacement="start"
                                sx={{ mr: 0, gap: 1, display: { xs: 'none', lg: 'flex' } }}
                            />
                        </Tooltip>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            disabled={!audition?._id}
                            onClick={() => openDetailsEditRef.current?.()}
                            sx={{
                                px: 3,
                                width: { xs: '100%', sm: 'auto' },
                                ...highlightButtonSx,
                            }}>
                            Edit
                        </Button>
                    </Box>
                </Box>

                {/* Hero — image only (text moved below), same dimensions feel as campaign feature image */}
                <Box
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        position: 'relative',
                        '& .MuiCard-root': { my: 0, height: 200, minHeight: 200 },
                    }}>
                    <CoverImageRenderer
                        key={`${auditionId}-${audition?._id ?? ''}`}
                        audition={audition}
                        editMode
                        minimalOverlays
                        onImageChange={(imageUrl, rawFile) => {
                            handleUpdateField([
                                { key: 'imageCover', value: [rawFile] },
                                { key: 'tempImageCover', value: imageUrl },
                            ]);
                        }}
                    />
                </Box>

                {/* Overview — same card chrome as campaign detail overview */}
                <Box>
                    <SectionLabel>Overview</SectionLabel>
                    <Box sx={{ width: '100%' }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                    lg: 'repeat(6, minmax(0, 1fr))',
                                },
                                gap: 2,
                                alignItems: 'stretch',
                                width: '100%',
                                minWidth: 0,
                            }}>
                            <OverviewCard
                                cardSx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}
                                icon={<EventAvailableOutlinedIcon sx={{ fontSize: 20 }} />}
                                label="Casting"
                                value=""
                                valueSx={{ display: 'none' }}
                                footer={
                                    <Chip
                                        label={isCastingClosed ? 'Casting closed' : 'Active casting call'}
                                        size="small"
                                        sx={brandPublishedPillSx}
                                    />
                                }
                            />
                            <OverviewCard
                                cardSx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}
                                icon={<BadgeOutlinedIcon sx={{ fontSize: 20 }} />}
                                label="Union"
                                value=""
                                valueSx={{ display: 'none' }}
                                footer={
                                    unionLabel ? (
                                        <Chip
                                            label={unionLabel}
                                            size="small"
                                            sx={{ ...brandPublishedPillSx, textTransform: 'none' }}
                                        />
                                    ) : (
                                        <Chip label="None" size="small" sx={nonePillSx} />
                                    )
                                }
                            />
                            <OverviewCard
                                cardSx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}
                                icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
                                label="Tags"
                                value=""
                                valueSx={{ display: 'none' }}
                                footer={
                                    hasTagValues ? (
                                        <CustomChipGroup
                                            tags={tagPills}
                                            clickable={false}
                                            withNiceColor={false}
                                            tagSx={brandPublishedPillSx}
                                            withWrap
                                        />
                                    ) : (
                                        <Chip label="None" size="small" sx={nonePillSx} />
                                    )
                                }
                            />
                            <OverviewCard
                                cardSx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}
                                icon={<CategoryOutlinedIcon sx={{ fontSize: 20 }} />}
                                label="Gig type"
                                value=""
                                valueSx={{ display: 'none' }}
                                footer={
                                    gigTypePills.length ? (
                                        <CustomChipGroup
                                            tags={gigTypePills}
                                            clickable={false}
                                            withNiceColor={false}
                                            tagSx={brandPublishedPillSx}
                                            withWrap
                                        />
                                    ) : (
                                        <Chip label="None" size="small" sx={nonePillSx} />
                                    )
                                }
                            />
                            <OverviewCard
                                cardSx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}
                                icon={<GpsFixedOutlined sx={{ fontSize: 20 }} />}
                                label="Location"
                                value=""
                                valueSx={{ display: 'none' }}
                                footer={<Chip label={locationLabel} size="small" sx={brandPublishedPillSx} />}
                            />
                            <OverviewCard
                                cardSx={{ width: '100%', maxWidth: 'none', minWidth: 0 }}
                                icon={<PermMediaOutlinedIcon sx={{ fontSize: 20 }} />}
                                label="Required media"
                                value=""
                                valueSx={{ display: 'none' }}
                                footer={
                                    hasRequiredMediaValues ? (
                                        <CustomChipGroup
                                            tags={requiredMediaChipLabels}
                                            clickable={false}
                                            withNiceColor={false}
                                            tagSx={brandPublishedPillSx}
                                            withWrap
                                        />
                                    ) : (
                                        <Chip label="None" size="small" sx={nonePillSx} />
                                    )
                                }
                            />
                        </Box>
                    </Box>
                </Box>

                {audition.synopsis ? (
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                        <SectionLabel>Audition brief</SectionLabel>
                        <RichTextViewer
                            content={audition.synopsis}
                            textColor="rgba(255,255,255,0.82)"
                            textStyle={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 15,
                                lineHeight: 1.7,
                                padding: 0,
                            }}
                        />
                    </Box>
                ) : null}

                {audition?._id ? (
                    <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                        <SectionLabel>Team</SectionLabel>
                        <ResourceTeamPanel resourceType="audition" resourceId={String(audition._id)} />
                    </Box>
                ) : null}

                <ManageAuditionContainer
                    audition={audition}
                    onRegisterOpenDetailsEditModal={registerOpenDetailsEdit}
                    applications={applications || []}
                    loading={loading}
                    error={error}
                    pagination={pagination}
                    filterStatus={filterStatus}
                    sortOption={sortOption}
                    roleFilter={roleFilter}
                    onSearchChange={handleSearchChange}
                    onFilterStatusChange={handleFilterStatusChange}
                    onSortOptionChange={handleSortOptionChange}
                    onRoleFilterChange={handleRoleFilterChange}
                    onRowsChange={handleRowsChange}
                    onPageChange={handlePageChange}
                    onEditRolesClick={() => undefined}
                    onViewDetailsClick={() => {}}
                    onAcceptApplication={handleAcceptApplication}
                    onRejectApplication={handleRejectApplication}
                    onMessageApplicant={handleMessageApplicant}
                    onArchiveApplication={handleArchiveApplication}
                    onCastApplicant={handleCastApplicant}
                    onClearSearch={() => {
                        forceNoRefresh.current = true;
                        setSearchTerm(undefined);
                        getData(true);
                    }}
                    searchLoading={searching}
                    onImageChange={(imageUrl, rawFile) => {
                        handleUpdateField([
                            { key: 'imageCover', value: [rawFile] },
                            { key: 'tempImageCover', value: imageUrl },
                        ]);
                    }}
                    layoutVariant="brand"
                    postDeleteNavigateTo={ROUTES.BRAND_CAMPAIGNS}
                    omitHeroSection
                    afterRolesContent={
                        <LinkBox
                            link={generateShareLink('audition', audition, true)}
                            sx={{
                                width: '100%',
                                my: 0,
                                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                border: '1px solid rgba(255,255,255,0.06)',
                            }}
                        />
                    }
                    roleDialogPaperSx={{
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                        color: '#fff',
                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                        backgroundImage: 'none',
                    }}
                />
            </Box>

            <DialogueSheet
                open={publishDialogOpen}
                onClose={() => setPublishDialogOpen(false)}
                onAgreeClicked={() => {
                    dispatch(publishAudition(audition._id));
                    setPublishDialogOpen(false);
                }}
                onDisagreeClick={() => setPublishDialogOpen(false)}
                header="Publish Audition"
                body={`This audition has ${applicationCount} applications. Are you sure you want to publish it?`}
                positiveText="Publish"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: t.tag_white,
                }}
            />

            <DialogueSheet
                open={unpublishDialogOpen}
                onClose={() => setUnpublishDialogOpen(false)}
                onAgreeClicked={() => {
                    dispatch(unPublishAudition(audition._id));
                    setUnpublishDialogOpen(false);
                }}
                onDisagreeClick={() => setUnpublishDialogOpen(false)}
                header="Unpublish Audition"
                body={`This audition has ${applicationCount} applications. Are you sure you want to unpublish it?`}
                positiveText="Unpublish"
                negativeText="Cancel"
                negtiveTextStyle={{
                    color: t.tag_white,
                }}
            />
        </MetaTags>
    );
}
